import { supabase } from '@/lib/supabase';
import { convertAmount } from '@/lib/currency';

// ─────────────────────────────────────────────────────────────
// « Mes gains » — ventes réellement encaissées par un commerce.
//
// Source : zifek_payments (method shop_feexpay / booking_feexpay, status paid)
//   • amount            → montant encaissé (brut)
//   • commission_amount → frais plateforme déduits
//   • net               → ce qui est arrivé sur le portefeuille Zifek
//
// Aucune donnée n'est écrite ici : c'est un récapitulatif de lecture.
// ─────────────────────────────────────────────────────────────

export type EarningKind = 'boutique' | 'reservation';

export interface EarningLine {
  id: number;
  reference: string;
  kind: EarningKind;
  label: string;
  client: string | null;
  date: string;
  gross: number;
  fee: number;
  net: number;
  currency: string;
}

export interface EarningsSummary {
  gross: number;
  fees: number;
  net: number;
  count: number;
  currency: string;
  shopCount: number;
  bookingCount: number;
  shopNet: number;
  bookingNet: number;
  lines: EarningLine[];
}

function toNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export async function fetchMerchantEarnings(
  merchantId: number,
  mainCurrency: string,
  limit = 200,
): Promise<EarningsSummary> {
  const empty: EarningsSummary = {
    gross: 0, fees: 0, net: 0, count: 0, currency: mainCurrency,
    shopCount: 0, bookingCount: 0, shopNet: 0, bookingNet: 0, lines: [],
  };
  if (!merchantId) return empty;

  try {
    const { data, error } = await supabase
      .from('zifek_payments')
      .select('id, amount, currency, method, status, reference, payload, created_at')
      .eq('user_id', merchantId)
      .in('method', ['shop_feexpay', 'booking_feexpay'])
      .eq('status', 'paid')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    const rows = (data || []) as {
      id: number;
      amount: number | null;
      currency: string | null;
      method: string | null;
      reference: string | null;
      payload: string | null;
      created_at: string | null;
    }[];

    const lines: EarningLine[] = rows.map((row) => {
      let payload: Record<string, unknown> = {};
      try {
        payload = row.payload ? JSON.parse(String(row.payload)) : {};
      } catch {
        payload = {};
      }

      const gross = toNumber(row.amount);
      const fee = toNumber(payload.commission_amount);
      const kind: EarningKind = String(row.method) === 'booking_feexpay' ? 'reservation' : 'boutique';
      const orderId = toNumber(payload.order_id);
      const appointmentId = toNumber(payload.appointment_id);

      return {
        id: Number(row.id),
        reference: String(row.reference || ''),
        kind,
        label: kind === 'reservation'
          ? `Réservation #${appointmentId || row.id}`
          : `Commande #${orderId || row.id}`,
        client: (payload.customer_name as string) || null,
        date: String(row.created_at || ''),
        gross,
        fee,
        net: Math.max(0, round2(gross - fee)),
        currency: String(row.currency || mainCurrency || 'MAD'),
      };
    });

    const summary: EarningsSummary = { ...empty, lines, count: lines.length };

    lines.forEach((line) => {
      const convertedGross = convertAmount(line.gross, line.currency, mainCurrency) ?? line.gross;
      const convertedFee = convertAmount(line.fee, line.currency, mainCurrency) ?? line.fee;
      const convertedNet = convertAmount(line.net, line.currency, mainCurrency) ?? line.net;

      summary.gross += convertedGross;
      summary.fees += convertedFee;
      summary.net += convertedNet;

      if (line.kind === 'reservation') {
        summary.bookingCount += 1;
        summary.bookingNet += convertedNet;
      } else {
        summary.shopCount += 1;
        summary.shopNet += convertedNet;
      }
    });

    summary.gross = round2(summary.gross);
    summary.fees = round2(summary.fees);
    summary.net = round2(summary.net);
    summary.shopNet = round2(summary.shopNet);
    summary.bookingNet = round2(summary.bookingNet);

    return summary;
  } catch {
    return empty;
  }
}